import { useCallback, useEffect, useRef, useState } from 'react';
import { streamJsonSseWithRetry } from '../../../utils/SseStreamUtil';
import { send_request } from '../../../utils/Request';
import config from '../../../config';

const TERMINAL_STATUSES = new Set(['SUCCESS', 'SUCCESS_END', 'FAIL', 'TERMINATED']);
const ACTIVE_POLL_INTERVAL = 2000;
const IDLE_POLL_INTERVAL = 10000;
const IDLE_THRESHOLD_MS = 60000;
const TERMINAL_CONFIRM_POLLS = 3;

/**
 * Hook that provides live session progress via SSE (primary) with smart polling fallback.
 *
 * @param {Object} params
 * @param {string} params.sessionId
 * @param {boolean} params.enabled
 * @returns {{ connectionState, componentStatuses, sessionStatus, lastUpdated, isTerminal }}
 */
export default function useSessionProgress({ sessionId, enabled }) {
  const [connectionState, setConnectionState] = useState('idle');
  const [componentStatuses, setComponentStatuses] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isTerminal, setIsTerminal] = useState(false);

  // Refs for polling mode state
  const lastChangeTimeRef = useRef(Date.now());
  const lastSignatureRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const usingPollingRef = useRef(false);
  const terminalCountRef = useRef(0);
  const cancelledRef = useRef(false);

  const updateFromEvent = useCallback((data, isCompleted = false) => {
    if (!data) return;
    setComponentStatuses(data.components);
    setSessionStatus(data.status);
    setLastUpdated(data.lastUpdated);
    if (isCompleted) {
      setIsTerminal(true);
    }
  }, []);

  // ── SSE mode ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !sessionId || usingPollingRef.current) return;

    const abortController = new AbortController();
    cancelledRef.current = false;

    const sseUrl = `${config.API_ADDRESS}/project-service/project-session/${sessionId}/events`;

    (async () => {
      const result = await streamJsonSseWithRetry({
        url: sseUrl,
        abortController,
        onEvent: (eventName, parsed) => {
          if (cancelledRef.current) return;
          if (eventName === 'session.status.updated') {
            updateFromEvent(parsed, false);
          } else if (eventName === 'session.completed') {
            updateFromEvent(parsed, true);
          }
        },
        shouldReconnect: () => !cancelledRef.current && !usingPollingRef.current,
        onConnectionStateChange: (state) => {
          if (!cancelledRef.current) setConnectionState(state);
        },
        maxRetries: 2,
        logLabel: `session-progress-${sessionId}`,
      });

      // If SSE failed permanently and we're still enabled, fall back to polling
      if (!cancelledRef.current && result?.connectionFailed) {
        usingPollingRef.current = true;
        startPolling();
      }
    })();

    return () => {
      cancelledRef.current = true;
      abortController.abort();
    };
  }, [sessionId, enabled]);

  // ── Polling mode (fallback) ───────────────────────────────────────────
  const startPolling = useCallback(() => {
    setConnectionState('polling');
    lastChangeTimeRef.current = Date.now();
    terminalCountRef.current = 0;

    const scheduleNextPoll = (intervalMs) => {
      pollTimeoutRef.current = setTimeout(poll, intervalMs);
    };

    const poll = async () => {
      if (cancelledRef.current) return;

      try {
        const json = await send_request(
          `project-service/project-session/${sessionId}/components-status`,
          '',
          {},
          ''
        );

        if (cancelledRef.current) return;
        if (!json?.data) {
          scheduleNextPoll(ACTIVE_POLL_INTERVAL);
          return;
        }

        const signature = JSON.stringify(json.data);
        if (signature !== lastSignatureRef.current) {
          lastSignatureRef.current = signature;
          lastChangeTimeRef.current = Date.now();
          updateFromEvent(json.data, false);
        }

        // Terminal confirmation buffer — require N consecutive terminal polls
        if (TERMINAL_STATUSES.has(json.data.status)) {
          terminalCountRef.current++;
          if (terminalCountRef.current >= TERMINAL_CONFIRM_POLLS) {
            updateFromEvent(json.data, true);
            stopPolling();
            return;
          }
        } else {
          terminalCountRef.current = 0;
        }

        // Adaptive interval: back off if idle
        const idleMs = Date.now() - lastChangeTimeRef.current;
        const nextInterval =
          idleMs > IDLE_THRESHOLD_MS ? IDLE_POLL_INTERVAL : ACTIVE_POLL_INTERVAL;

        scheduleNextPoll(nextInterval);
      } catch (e) {
        if (!cancelledRef.current) {
          scheduleNextPoll(ACTIVE_POLL_INTERVAL);
        }
      }
    };

    // Initial poll immediately
    poll();
  }, [sessionId, updateFromEvent]);

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount or disable
  useEffect(() => {
    if (!enabled) {
      stopPolling();
      usingPollingRef.current = false;
      cancelledRef.current = true;
    }
    return () => {
      stopPolling();
      cancelledRef.current = true;
    };
  }, [enabled, stopPolling]);

  return {
    connectionState,
    componentStatuses,
    sessionStatus,
    lastUpdated,
    isTerminal,
  };
}
